/**
 * Hero section Ask Guiri chat preview — fully self-contained with inline styles.
 * The marketing landing page does NOT load Tailwind utilities, so all layout
 * must use inline styles. Colours reference the --hy-* CSS tokens from
 * src/design-tokens/tokens.css which ARE loaded on the marketing route.
 *
 * Scenario: NIE application question, thread preset, 4 official sources.
 * Matches the design file: Marketing Landing v2 / askguiri-components.jsx.
 */
import * as React from "react";
import icon128Url from "@/assets/icons/icon-128.webp";

const SOURCES = [
  { n: 1, title: "BOE — Real Decreto 240/2007",     host: "boe.es",                                    url: "https://www.boe.es/buscar/doc.php?id=BOE-A-2007-4118" },
  { n: 2, title: "Sede Electrónica · Solicitud NIE", host: "sede.administracionespublicas.gob.es",      url: "https://sede.administracionespublicas.gob.es" },
  { n: 3, title: "Hacienda · IRPF resident",         host: "agenciatributaria.gob.es",                  url: "https://www.agenciatributaria.gob.es" },
  { n: 4, title: "Policía Nacional · NIE",           host: "policia.es",                                url: "https://www.policia.es" },
];

/** Shared mono-caps style — mirrors the Mono primitive without Tailwind. */
const MONO: React.CSSProperties = {
  fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

/** Saffron squiggle SVG reused from Body.tsx — injected via <style>. */
const SQUIGGLE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 5' width='12' height='5'><path d='M0 3 Q3 0 6 3 T12 3' stroke='%23EB9D2A' stroke-width='1.4' fill='none' stroke-linecap='round'/></svg>\")";

export function HeroChatPreview({
  ariaLabel,
  userText,
  tldr,
  botHtml,
}: {
  ariaLabel: string;
  userText: string;
  tldr: string;
  botHtml: string;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <aside className="mk-playground-wrap" aria-label={ariaLabel}>
      <div className="mk-chat-card">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* ── User message ── right-aligned bubble */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <span style={{ ...MONO, fontSize: 10, fontWeight: 500, color: "var(--hy-faint)" }}>
              you · 22:28
            </span>
            <div style={{
              background: "var(--hy-user-bg)",
              border: "1px solid var(--hy-hair)",
              borderRadius: "14px 14px 4px 14px",
              padding: "8px 14px",
              maxWidth: "82%",
              fontSize: 14,
              lineHeight: 1.45,
              color: "var(--hy-ink)",
              fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
            }}>
              {userText}
            </div>
          </div>

          {/* ── Assistant header ── avatar + wordmark + Pro badge */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 4 }}>
            <img
              src={icon128Url}
              alt=""
              aria-hidden
              decoding="async"
              style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            />
            <span style={{
              fontFamily: '"Caprasimo", Georgia, serif',
              fontSize: 22,
              letterSpacing: "-0.01em",
              lineHeight: 1,
              color: "var(--hy-ink)",
            }}>
              guiri<span style={{ color: "var(--hy-saffron)" }}>.</span>
            </span>
            <span style={{
              ...MONO,
              fontSize: 10,
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: 9999,
              border: "1px solid var(--hy-hair)",
              color: "var(--hy-mute)",
              background: "transparent",
              lineHeight: 1.2,
            }}>
              Pro
            </span>
          </div>

          {/* ── TL;DR block ── saffron pill, label inline with text */}
          <div
            role="note"
            aria-label="TL;DR summary"
            style={{
              background: "var(--hy-saffron-soft)",
              borderRadius: 10,
              padding: "10px 12px",
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
            }}
          >
            <span style={{
              ...MONO,
              fontSize: 10,
              fontWeight: 700,
              color: "var(--hy-saffron-deep)",
              marginTop: 2,
              flexShrink: 0,
            }}>
              TL;DR
            </span>
            <div style={{
              fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
              fontSize: 14.5,
              lineHeight: 1.45,
              color: "var(--hy-ink)",
            }}>
              {tldr}
            </div>
          </div>

          {/* ── Body ── prose with saffron squiggle links */}
          <div style={{
            fontFamily: '"IBM Plex Sans", system-ui, sans-serif',
            fontSize: 14,
            lineHeight: 1.6,
            color: "var(--hy-ink)",
          }}>
            <style>{`
              .hy-hero-body a {
                color: var(--hy-ink);
                font-weight: 500;
                text-decoration: none;
                background-image: ${SQUIGGLE};
                background-repeat: repeat-x;
                background-position: 0 100%;
                background-size: 12px 5px;
                padding-bottom: 5px;
              }
              .hy-hero-body a:hover { color: var(--hy-saffron-deep); }
              .hy-hero-body sup {
                font-family: "IBM Plex Mono", ui-monospace, monospace;
                font-size: 10px;
                font-weight: 500;
                vertical-align: super;
                line-height: 1;
                padding: 0 3px;
                color: var(--hy-faint);
              }
            `}</style>
            <p
              className="hy-hero-body"
              dangerouslySetInnerHTML={{ __html: botHtml }}
              style={{ margin: "0 0 6px" }}
            />
          </div>

          {/* ── Sources ── footer row + expandable list */}
          <div style={{ borderTop: "1px solid var(--hy-hair)", paddingTop: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ ...MONO, fontSize: 10, fontWeight: 500, color: "var(--hy-faint)" }}>
                Sources
              </span>
              <span style={{ fontSize: 12, color: "var(--hy-mute)", fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}>
                {SOURCES.length} · all from official sources
              </span>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                style={{
                  ...MONO,
                  fontSize: 10,
                  color: "var(--hy-mute)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: 0,
                  fontWeight: 500,
                }}
              >
                {open ? "Hide ↑" : "Show all ↓"}
              </button>
            </div>

            {open && (
              <ul style={{ margin: "10px 0 0", padding: 0, listStyle: "none" }}>
                <li style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6, padding: "3px 8px", borderRadius: 6, background: "var(--hy-saffron-soft)" }}>
                  <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="var(--hy-saffron-deep)" strokeWidth={1.2} aria-hidden>
                    <path d="M12 2 L3 6 V12 C3 17 7 21 12 22 C17 21 21 17 21 12 V6 Z" />
                    <path d="M8 12 L11 15 L16 9" />
                  </svg>
                  <span style={{ ...MONO, fontSize: 10, color: "var(--hy-saffron-deep)" }}>Verified · official sources only</span>
                </li>
                {SOURCES.map((s) => (
                  <li key={s.n} style={{ display: "flex", alignItems: "center", gap: 12, borderBottom: "1px solid var(--hy-hair)", padding: "6px 0" }}>
                    <span style={{ ...MONO, fontSize: 10, color: "var(--hy-faint)", width: 22, flexShrink: 0 }}>
                      [{s.n}]
                    </span>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ flex: 1, fontSize: 12.5, color: "var(--hy-ink)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: '"IBM Plex Sans", system-ui, sans-serif' }}
                    >
                      {s.title}
                    </a>
                    <span style={{ ...MONO, fontSize: 10, color: "var(--hy-faint)", flexShrink: 0, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.host}
                    </span>
                    <span style={{ fontSize: 11, color: "var(--hy-faint)" }}>↗</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      </div>
    </aside>
  );
}
